import React from 'react';
import * as S from './LayoutAlert.styled';

export type LayoutAlertAction = {
  label: string,
  onClick: () => void,
};

type LayoutAlertProps = {
  icon: React.ReactNode,
  title: React.ReactNode,
  text?: React.ReactNode,
  /** Strip / icon / secondary-button colour. */
  accent: string,
  secondaryAction?: LayoutAlertAction,
  primaryAction?: LayoutAlertAction,
  /** Filled uses accent; outlined uses the app purple (storage “Increase”). */
  primaryVariant?: 'filled' | 'outlined',
  /** Ellipsis for one-line subjects; wrap for longer storage copy. */
  textEllipsis?: boolean,
  role?: string,
  /** Paint under RightBar / page UI instead of above the in-app loader. */
  belowOverlays?: boolean,
  /** Mix the strip with white so a strong accent (e.g. report blue) stays pale. */
  paleStrip?: boolean,
};

/**
 * Top layout alert strip. Sits above the in-app loader in z so the dim
 * stays full-bleed (no Y gap) while the strip stays undimmed and clickable.
 */
const LayoutAlert = ({
  icon,
  title,
  text,
  accent,
  secondaryAction,
  primaryAction,
  primaryVariant = 'filled',
  textEllipsis,
  role = 'status',
  belowOverlays,
  paleStrip,
}: LayoutAlertProps) => (
  <S.Container
    $accent={accent}
    $belowOverlays={belowOverlays}
    $paleStrip={paleStrip}
    role={role}
  >
    <S.Icon $accent={accent} aria-hidden>
      {icon}
    </S.Icon>
    <S.Body>
      <S.Title>{title}</S.Title>
      {text ? <S.Text $ellipsis={textEllipsis}>{text}</S.Text> : null}
    </S.Body>
    {(secondaryAction || primaryAction) ? (
      <S.Actions>
        {secondaryAction ? (
          <S.SecondaryBtn type="button" $accent={accent} onClick={secondaryAction.onClick}>
            {secondaryAction.label}
          </S.SecondaryBtn>
        ) : null}
        {primaryAction && primaryVariant === 'outlined' ? (
          <S.OutlinedBtn type="button" onClick={primaryAction.onClick}>
            {primaryAction.label}
          </S.OutlinedBtn>
        ) : null}
        {primaryAction && primaryVariant !== 'outlined' ? (
          <S.FilledBtn type="button" $accent={accent} onClick={primaryAction.onClick}>
            {primaryAction.label}
          </S.FilledBtn>
        ) : null}
      </S.Actions>
    ) : null}
  </S.Container>
);

export default LayoutAlert;
